import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { fetchAdminPayments, type AdminPaymentEmailOption, type AdminPaymentRecord, type AdminPaymentStatus, type AdminPaymentSummaryEntry, type AdminPaymentTransactionType } from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { useToast } from '@/hooks/use-toast';
import { formatAdminDateParts } from '@/components/admin/ordersWorkspace/utils';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const formatMoney = (amount: number | null | undefined, currency: string | null | undefined): string => {
  if (amount == null || currency == null || currency.trim().length === 0) {
    return '—';
  }
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const shouldShowOriginalAmount = (payment: AdminPaymentRecord): boolean => {
  if (payment.originalAmount == null) {
    return false;
  }

  const originalCurrency = (payment.originalCurrency ?? payment.currency).trim().toUpperCase();
  const paymentCurrency = payment.currency.trim().toUpperCase();

  if (originalCurrency !== 'USD') {
    return true;
  }

  return !(originalCurrency === paymentCurrency && payment.originalAmount === Math.abs(payment.signedAmount));
};

const renderSummaryLines = (summary: ReadonlyArray<AdminPaymentSummaryEntry>, field: 'debit' | 'credit' | 'total') => {
  if (summary.length === 0) {
    return <p className="text-sm text-muted-foreground">—</p>;
  }

  return (
    <div className="space-y-1">
      {summary.map((entry) => (
        <p key={`${field}-${entry.currency}`} className="text-sm font-medium">
          {formatMoney(entry[field], entry.currency)}
        </p>
      ))}
    </div>
  );
};

const AdminPaymentsPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const { toast } = useToast();

  const [payments, setPayments] = useState<ReadonlyArray<AdminPaymentRecord>>([]);
  const [transactionTypes, setTransactionTypes] = useState<ReadonlyArray<AdminPaymentTransactionType>>([]);
  const [statuses, setStatuses] = useState<ReadonlyArray<AdminPaymentStatus>>([]);
  const [emailOptions, setEmailOptions] = useState<ReadonlyArray<AdminPaymentEmailOption>>([]);
  const [summary, setSummary] = useState<ReadonlyArray<AdminPaymentSummaryEntry>>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState<string>('ALL');
  const [transactionType, setTransactionType] = useState<string>('ALL');
  const [paymentStatus, setPaymentStatus] = useState<string>('ALL');
  const [onlyConfirmed, setOnlyConfirmed] = useState(true);

  const hasFilters = useMemo(
    () => search.length > 0 || email !== 'ALL' || transactionType !== 'ALL' || paymentStatus !== 'ALL' || pageSize !== 50 || !onlyConfirmed,
    [email, onlyConfirmed, pageSize, paymentStatus, search, transactionType],
  );

  const visibleColumnCount = onlyConfirmed ? 7 : 8;

  const loadPayments = useCallback(async () => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchAdminPayments({
        page,
        pageSize,
        search: search || undefined,
        email: email === 'ALL' ? undefined : email,
        transactionType: transactionType === 'ALL' ? undefined : transactionType,
        status: onlyConfirmed ? 'COMPLETED' : paymentStatus === 'ALL' ? undefined : paymentStatus,
      });
      setPayments(result.payments);
      setTransactionTypes(result.filters.transactionTypes);
      setStatuses(result.filters.statuses);
      setEmailOptions(result.filters.emails);
      setSummary(result.summary);
      setPagination(result.pagination);
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to load admin payments', { error: err });
      const message = err instanceof Error ? err.message : 'Failed to load transactions.';
      setError(message);
      setStatus('error');
      toast({ title: 'Failed to load transactions', description: message, variant: 'destructive' });
    }
  }, [email, onlyConfirmed, page, pageSize, paymentStatus, search, toast, transactionType]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void loadPayments();
  }, [isAdmin, loadPayments, navigate]);

  const handleApplyFilters = async () => {
    analytics.track('button: ui.admin_payments_apply_filters', {
      search: searchInput || null,
      email: email === 'ALL' ? null : email,
      transactionType: transactionType === 'ALL' ? null : transactionType,
      status: onlyConfirmed ? 'COMPLETED' : paymentStatus === 'ALL' ? null : paymentStatus,
      onlyConfirmed,
      pageSize,
    });
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleResetFilters = async () => {
    analytics.track('button: ui.admin_payments_reset_filters');
    setSearchInput('');
    setSearch('');
    setEmail('ALL');
    setTransactionType('ALL');
    setPaymentStatus('ALL');
    setOnlyConfirmed(true);
    setPage(1);
    setPageSize(50);
  };

  const goToPrevious = () => {
    if (page > 1) {
      setPage((current) => Math.max(1, current - 1));
    }
  };

  const goToNext = () => {
    if (pagination.totalPages === 0) {
      return;
    }
    setPage((current) => Math.min(pagination.totalPages, current + 1));
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-10">
      <Card>
        <CardHeader>
          <CardTitle>Payment Transactions</CardTitle>
          <CardDescription>All records from the payment table for admin review.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <div className="space-y-2 xl:col-span-2">
              <Label htmlFor="payments-search">Search</Label>
              <Input
                id="payments-search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Email, name, order ID, transaction ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payments-email">Email</Label>
              <Select value={email} onValueChange={(value) => { setEmail(value); setPage(1); }}>
                <SelectTrigger id="payments-email">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {emailOptions.map((option) => (
                    <SelectItem key={option.id} value={option.email}>
                      {option.name ? `${option.name} (${option.email})` : option.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payments-type">Transaction type</Label>
              <Select value={transactionType} onValueChange={(value) => { setTransactionType(value); setPage(1); }}>
                <SelectTrigger id="payments-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All</SelectItem>
                  {transactionTypes.map((value) => (
                    <SelectItem key={value} value={value}>{value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!onlyConfirmed ? (
              <div className="space-y-2">
                <Label htmlFor="payments-status">Status</Label>
                <Select value={paymentStatus} onValueChange={(value) => { setPaymentStatus(value); setPage(1); }}>
                  <SelectTrigger id="payments-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All</SelectItem>
                    {statuses.map((value) => (
                      <SelectItem key={value} value={value}>{value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="payments-page-size">Page size</Label>
              <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                <SelectTrigger id="payments-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((value) => (
                    <SelectItem key={value} value={String(value)}>{value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Confirmed debit</CardTitle>
                <CardDescription>Only `COMPLETED` rows in current scope.</CardDescription>
              </CardHeader>
              <CardContent>
                {renderSummaryLines(summary, 'debit')}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Confirmed credit</CardTitle>
                <CardDescription>Only `COMPLETED` rows in current scope.</CardDescription>
              </CardHeader>
              <CardContent>
                {renderSummaryLines(summary, 'credit')}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Confirmed total</CardTitle>
                <CardDescription>Debit minus credit for `COMPLETED` rows.</CardDescription>
              </CardHeader>
              <CardContent>
                {renderSummaryLines(summary, 'total')}
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={() => void handleApplyFilters()} disabled={status === 'loading'}>
              {status === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Apply filters
            </Button>
            <Button type="button" variant="outline" onClick={() => void handleResetFilters()} disabled={!hasFilters && page === 1}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset filters
            </Button>
            <Button type="button" variant="outline" onClick={() => void loadPayments()} disabled={status === 'loading'}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={onlyConfirmed}
                onCheckedChange={(checked) => {
                  const nextValue = checked === true;
                  setOnlyConfirmed(nextValue);
                  setPage(1);
                  if (nextValue) {
                    setPaymentStatus('ALL');
                  }
                }}
              />
              <span>Only Confirmed</span>
            </label>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            {!error ? (
              <p className="text-sm text-muted-foreground">{pagination.total} transactions</p>
            ) : null}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Type</TableHead>
                  {!onlyConfirmed ? <TableHead>Status</TableHead> : null}
                  <TableHead>Amount</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status === 'loading' && payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="py-10 text-center text-sm text-muted-foreground">Loading transactions…</TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="py-10 text-center text-sm text-muted-foreground">No transactions found.</TableCell>
                  </TableRow>
                ) : payments.map((payment) => {
                  const createdAt = formatAdminDateParts(payment.createdAt);

                  return (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <p>{createdAt.date}</p>
                        <p>{createdAt.time}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{payment.user.name ?? 'Unknown user'}</p>
                        <p className="text-xs text-muted-foreground">{payment.user.email}</p>
                        <p className="text-xs text-muted-foreground">ID: {payment.user.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{payment.operationTitle}</p>
                        <p className="text-xs text-muted-foreground">{payment.transactionType} · {payment.transactionSource}</p>
                      </div>
                    </TableCell>
                    {!onlyConfirmed ? (
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{payment.status}</p>
                          <p className="text-xs text-muted-foreground">{payment.direction}</p>
                        </div>
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <div className="space-y-1">
                        <p className={payment.signedAmount >= 0 ? 'text-sm font-medium text-emerald-700' : 'text-sm font-medium text-rose-700'}>
                          {formatMoney(payment.signedAmount, payment.currency)}
                        </p>
                        {shouldShowOriginalAmount(payment) ? (
                          <p className="text-xs text-muted-foreground">
                            Original: {formatMoney(payment.originalAmount, payment.originalCurrency ?? payment.currency)}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {payment.order ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">{payment.order.displayCode}</p>
                          <Button type="button" size="sm" variant="outline" onClick={() => navigate('/admin/orders')}>
                            Open orders
                          </Button>
                        </div>
                      ) : <span className="text-sm text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{payment.provider}</p>
                        <p className="text-xs text-muted-foreground">{payment.shippingProvider ?? payment.shippingOption ?? '—'}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs text-muted-foreground" title={payment.reason ?? undefined}>
                      {payment.reason ?? '—'}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {pagination.totalPages === 0 ? 'No pages' : `Page ${pagination.page} of ${pagination.totalPages}`}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={goToPrevious} disabled={page <= 1 || status === 'loading'}>
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={goToNext} disabled={pagination.totalPages !== 0 && page >= pagination.totalPages || status === 'loading'}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPaymentsPage;
